import { ReactNode, useCallback, useEffect, useState } from 'react'
import {
    $createParagraphNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $setSelection,
    COMMAND_PRIORITY_CRITICAL,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list'
import { styled } from '@mui/material/styles'
import { Button, Divider, Menu, MenuItem, Stack, Tooltip, useTheme } from '@mui/material'
import { SubmitPlugin } from 'ComponentLibrary/src/molecules/TextEditor/SubmitPlugin'
import { CaretDownIcon, ListBulletsIcon, ListNumbersIcon, TextBIcon, TextItalicIcon } from '@phosphor-icons/react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, $isHeadingNode, HeadingNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { tokens } from 'ComponentLibrary'

export type ToolbarPluginCopy = {
    textStyle: string
    bold: string
    italic: string
    bulletedList: string
    numberedList: string
    headingOptions: {
        paragraph: string
        h2: string
        h3: string
        h4: string
        h5: string
    }
}

type ToolbarProps = {
    onSubmit?: (value: string, mentions: number[]) => void
    onCancel?: () => void
    enableBulletedList?: boolean
    enableNumberedList?: boolean
    enableHeadings?: boolean
    enableTextFormatting?: boolean
    copy?: Partial<ToolbarPluginCopy>
}

type HeadingOptionValue = 'paragraph' | 'h2' | 'h3' | 'h4' | 'h5'

const DEFAULT_TOOLBAR_PLUGIN_COPY: ToolbarPluginCopy = {
    textStyle: 'Textstil',
    bold: 'Fett',
    italic: 'Kursiv',
    bulletedList: 'Aufzählung',
    numberedList: 'Nummerierte Liste',
    headingOptions: {
        paragraph: 'Text',
        h2: 'Headline 1',
        h3: 'Headline 2',
        h4: 'Headline 3',
        h5: 'Headline 4',
    },
}

// h1 looks comically big in our messages, so we start at h2 for now
function getHeadingOptions(copy: ToolbarPluginCopy): { value: HeadingOptionValue; label: string }[] {
    return [
        { value: 'paragraph', label: copy.headingOptions.paragraph },
        { value: 'h2', label: copy.headingOptions.h2 },
        { value: 'h3', label: copy.headingOptions.h3 },
        { value: 'h4', label: copy.headingOptions.h4 },
        { value: 'h5', label: copy.headingOptions.h5 },
    ]
}

type ToolbarButtonProps = {
    tooltip: string
    selected?: boolean
    onClick: () => void
    children: ReactNode
}

function ToolbarButton({ tooltip, selected, onClick, children }: ToolbarButtonProps) {
    return (
        <Tooltip title={tooltip} placement="top">
            <StyledButton onClick={onClick} selected={selected} aria-label={tooltip}>
                {children}
            </StyledButton>
        </Tooltip>
    )
}

// This is heavily inspired by the Lexical Playground: https://playground.lexical.dev/
const ToolbarPlugin = ({
    onSubmit,
    onCancel,
    enableBulletedList = true,
    enableNumberedList = false,
    enableHeadings = false,
    enableTextFormatting = true,
    copy,
}: ToolbarProps) => {
    const theme = useTheme()
    const resolvedCopy = {
        ...DEFAULT_TOOLBAR_PLUGIN_COPY,
        ...copy,
        headingOptions: {
            ...DEFAULT_TOOLBAR_PLUGIN_COPY.headingOptions,
            ...copy?.headingOptions,
        },
    }
    const headingOptions = getHeadingOptions(resolvedCopy)
    const [editor] = useLexicalComposerContext()
    const [isBold, setIsBold] = useState(false)
    const [isItalic, setIsItalic] = useState(false)
    const [isBulletedList, setIsBulletedList] = useState(false)
    const [isNumberedList, setIsNumberedList] = useState(false)
    const [blockType, setBlockType] = useState<string>('paragraph')
    const [headingTag, setHeadingTag] = useState<string | null>(null)
    const [headingMenuAnchorEl, setHeadingMenuAnchorEl] = useState<HTMLElement | null>(null)
    const isHeadingMenuOpen = Boolean(headingMenuAnchorEl)

    // Make sure the selection is always cleared when the toolbar is mounted
    useEffect(() => {
        editor.update(() => {
            $setSelection(null)
        })
    }, [editor])

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
            setIsBold(selection.hasFormat('bold'))
            setIsItalic(selection.hasFormat('italic'))

            const anchorNode = selection.anchor.getNode()
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                          const parent = e.getParent()
                          return parent !== null && $isRootOrShadowRoot(parent)
                      })

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow()
            }

            const elementKey = element.getKey()
            const elementDOM = editor.getElementByKey(elementKey)
            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
                    const type = parentList ? parentList.getListType() : element.getListType()
                    setIsBulletedList(type === 'bullet')
                    setIsNumberedList(type === 'number')
                } else {
                    setIsBulletedList(false)
                    setIsNumberedList(false)
                }

                if ($isHeadingNode(element)) {
                    const headingNode: HeadingNode = element
                    setBlockType('heading')
                    setHeadingTag(headingNode.getTag())
                } else {
                    setBlockType('paragraph')
                    setHeadingTag(null)
                }
            }
        }
        return true
    }, [setIsBold, setIsItalic, setIsBulletedList, setIsNumberedList, setBlockType, setHeadingTag, editor])

    const formatBulletList = () => {
        if (isBulletedList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
    }

    const formatNumberedList = () => {
        if (isNumberedList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
    }

    // This is needed so selecting a heading BEFORE any text is written is working correctly
    const setHeadingBlockType = (value: HeadingOptionValue) => {
        const applyBlockTypeToSelection = () => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) {
                return false
            }

            if (value === 'paragraph') {
                $setBlocksType(selection, () => $createParagraphNode())
                return true
            }

            $setBlocksType(selection, () => $createHeadingNode(value))
            return true
        }

        editor.focus()
        editor.update(() => {
            if (applyBlockTypeToSelection()) {
                return
            }

            const root = $getRoot()
            if (root.getChildrenSize() === 0) {
                root.append($createParagraphNode())
            }
            root.selectStart()
            applyBlockTypeToSelection()
        })
    }

    const normalizedHeadingTag = headingTag === 'h1' ? 'h2' : headingTag
    const selectedHeadingOption = headingOptions.find((option) => option.value === normalizedHeadingTag)
    const selectedHeadingValue: HeadingOptionValue =
        blockType === 'heading' && selectedHeadingOption ? selectedHeadingOption.value : 'paragraph'
    const selectedHeadingLabel =
        headingOptions.find((option) => option.value === selectedHeadingValue)?.label ?? resolvedCopy.headingOptions.paragraph

    // Make sure the toolbar is always reflecting the correct state
    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $updateToolbar()
                    return false
                },
                COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar()
                })
            })
        )
    }, [editor, $updateToolbar])

    return (
        <ToolbarContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" marginRight="10px">
                <Stack direction="row" gap={theme.spacing(1)}>
                    {enableHeadings && (
                        <>
                            <Tooltip title={resolvedCopy.textStyle} placement="top">
                                <HeadingSelectButton
                                    aria-label={resolvedCopy.textStyle}
                                    onClick={(event) => setHeadingMenuAnchorEl(event.currentTarget)}
                                    endIcon={<CaretDownIcon size={14} />}
                                >
                                    {selectedHeadingLabel}
                                </HeadingSelectButton>
                            </Tooltip>
                            <Menu
                                anchorEl={headingMenuAnchorEl}
                                open={isHeadingMenuOpen}
                                onClose={() => setHeadingMenuAnchorEl(null)}
                                MenuListProps={{ dense: true }}
                            >
                                {headingOptions.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        selected={option.value === selectedHeadingValue}
                                        onClick={() => {
                                            setHeadingBlockType(option.value)
                                            setHeadingMenuAnchorEl(null)
                                        }}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />
                        </>
                    )}

                    {enableTextFormatting && (
                        <>
                            <ToolbarButton
                                tooltip={resolvedCopy.bold}
                                onClick={() => {
                                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
                                }}
                                selected={isBold}
                            >
                                <TextBIcon size={18} />
                            </ToolbarButton>
                            <ToolbarButton
                                tooltip={resolvedCopy.italic}
                                onClick={() => {
                                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
                                }}
                                selected={isItalic}
                            >
                                <TextItalicIcon size={18} />
                            </ToolbarButton>
                        </>
                    )}
                    {enableBulletedList && (
                        <ToolbarButton tooltip={resolvedCopy.bulletedList} onClick={formatBulletList} selected={isBulletedList}>
                            <ListBulletsIcon size={18} />
                        </ToolbarButton>
                    )}
                    {enableNumberedList && (
                        <ToolbarButton tooltip={resolvedCopy.numberedList} onClick={formatNumberedList} selected={isNumberedList}>
                            <ListNumbersIcon size={18} />
                        </ToolbarButton>
                    )}
                </Stack>
                {onSubmit && <SubmitPlugin onSubmit={onSubmit} onCancel={onCancel} submitVariant={'toolbar'} />}
            </Stack>
        </ToolbarContainer>
    )
}

const ToolbarContainer = styled('div')`
    display: flex;
    border: 1px solid #ccc;
    background: #f7f8ff;
    padding: 4px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    vertical-align: middle;
    overflow: hidden;
    height: 44px;
    position: sticky;
    z-index: 2;
    gap: 8px;
`

const StyledButton = styled(Button)<{ selected?: boolean }>`
    width: 36px;
    min-width: 0;
    background-color: ${({ selected }) => (selected ? 'rgba(8, 19, 75, 0.1)' : 'transparent')};
    :hover {
        background-color: rgba(8, 19, 75, 0.1);
    }
`

const HeadingSelectButton = styled(Button)`
    min-width: 142px;
    height: 36px;
    justify-content: space-between;
    color: ${tokens.ColorTextBold};
    border: none;
    text-transform: none;
    padding: 0 ${tokens.SizingSpacing100};
    :hover {
        background-color: rgba(8, 19, 75, 0.1);
    }
`

export { ToolbarPlugin }
